import {
  useEffect,
  useState,
  type ChangeEvent,
  type FunctionComponent,
  type SyntheticEvent,
} from 'react';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { isMobile } from 'react-device-detect';
import { track } from '@fff-web/fff-analytics';
import { Button, showErrorToast, showSuccessToast } from '@fff-web/fff-ui-shared';
import { postReview } from '../../services/submitReviewRequest';
import formValidation from '../../utils/FormValidation/formValidation';
import StarRatingButton from '../StarRatingButton';
import ErrorMessage from './ErrorMessage/ErrorMessage';
import * as styles from './SubmissionForm.module.scss';

type FormProps = {
  brandName: string;
  name: string;
  imgUrl: string;
  productId: number;
  defaultRating?: number;
  fingerPrint: string;
  sku: string;
  context: string;
  onSuccess?: (response: unknown) => void;
  closeModal: () => void;
  env: 'dev' | 'prod';
};
export const CHARACTER_LIMIT = 50;
const PLACEHOLDER_TEXT =
  'What did you like or dislike about this product? \n \nAvoid inappropriate language, personal info, references to the other details or copyrighted comments.';

const REVIEW_LINK =
  'https://support.fabfitfun.com/?a=Ratings-and-Reviews-Guidelines---id--WlO3OsEqRNOFUjn2eClJdg';

const SubmissionForm: FunctionComponent<FormProps> = ({
  brandName,
  name,
  imgUrl,
  productId,
  defaultRating,
  fingerPrint,
  sku,
  context,
  onSuccess,
  closeModal,
  env,
}) => {
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [formRating, setRating] = useState(defaultRating || 0);
  const [reviewText, setReviewText] = useState('');
  const [errorsForm, setErrors] = useState({
    rating: false,
    text: false,
  });

  useEffect(() => {
    track({
      trackName: 'Review Started',
      properties: {
        sku: sku,
        brand_name: brandName,
        product_name: name,
        product_id: productId,
        experience: context,
      },
    });
  }, []);

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setButtonsDisabled(true);
    const errors = formValidation(reviewText, formRating);
    const { rating, text } = errors;
    if (rating || text) setErrors(errors);
    else {
      try {
        const response = await postReview(productId, formRating, reviewText, fingerPrint, env);
        track({
          trackName: 'Review Submitted',
          properties: {
            sku: sku,
            brand_name: brandName,
            product_name: name,
            product_id: productId,
            rating: formRating,
            experience: context,
            review_text: reviewText.length > 0,
          },
        });
        showSuccessToast('Review Successful', 'Your review has been submitted for approval.');
        if (onSuccess) {
          onSuccess(response);
        }
        closeModal();
      } catch {
        showErrorToast(
          'Review Unsuccessful',
          'We had trouble submitting your review. Please try again later.',
        );
        closeModal();
      }
    }
    setButtonsDisabled(false);
  };

  const handleRatingChange = (rating: number) => {
    if (errorsForm.rating) setErrors({ ...errorsForm, rating: false });
    setRating(rating);
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (errorsForm.text) setErrors({ ...errorsForm, text: false });
    setReviewText(e.target.value);
  };

  const getReviewFeedback = () => {
    if (reviewText.length === 0)
      return `${reviewText.length} characters. ${CHARACTER_LIMIT} minimum characters`;
    if (reviewText.length < 50)
      return `${reviewText.length} characters (${CHARACTER_LIMIT} minimum characters)`;
    if (reviewText.length >= 50)
      return <FontAwesomeIcon icon={faCheckCircle} color="green" size="lg" />;
  };

  return (
    <form className={styles.container} onSubmit={handleSubmit} data-testid="submission-form">
      <h1 className={styles.title}>Write a Review</h1>
      <div className={styles.productSummaryContainer} data-testid="submission-container">
        <img className={styles.productImage} src={imgUrl} alt={brandName} />
        <div className={styles.productTitleContainer}>
          <h4 className={styles.productBrandName}>{brandName}</h4>
          <p className={styles.productName}>{name}</p>
        </div>
      </div>
      <h3 className={styles.secondaryTitle}>Star Rating*</h3>
      <div className={styles.starsContainer} data-testid="submission-container">
        <StarRatingButton
          containerWidth={315}
          onClick={handleRatingChange}
          defaultValue={defaultRating}
        />
        <div className={styles.errorContainer} data-testid="submission-container">
          {errorsForm.rating ? <ErrorMessage message="This is a required field." /> : <div />}
        </div>
      </div>
      <h3 className={styles.secondaryTitle}>Review</h3>
      <textarea
        className={cx(styles.textArea, {
          [styles.errorTextArea]: errorsForm.text,
        })}
        placeholder={PLACEHOLDER_TEXT}
        onChange={handleInputChange}
        value={reviewText}
      />
      <div className={styles.infoContainer} data-testid="submission-container">
        {errorsForm.text ? <ErrorMessage message="Minimum character count not met." /> : <div />}
        <div className={styles.infoLight} data-testid="review-feedback-text">
          {getReviewFeedback()}
        </div>
      </div>
      <h5 className={styles.paragraph}>
        By submitting, I agree to the&nbsp;
        <a href={REVIEW_LINK} target="_blank" rel="noopener noreferrer">
          Review Guidelines.
        </a>
      </h5>
      <p className={styles.paragraphLight}>
        Please note, you may only write one review per product. Review cannot be edited once
        submitted.
      </p>
      <div className={styles.buttonsContainer} data-testid="submission-container">
        <Button
          variation="black-outline"
          disabled={buttonsDisabled}
          onClick={closeModal}
          width={isMobile ? 160 : undefined}
        >
          Cancel
        </Button>
        <Button disabled={buttonsDisabled} type="submit" width={isMobile ? 160 : undefined}>
          Submit
        </Button>
      </div>
    </form>
  );
};

export default SubmissionForm;
